import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, functions } from "../firebase";
import { getFlagEmoji } from "../utils/flagEmoji";
import { formatDate } from "../utils/formatDate";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";

export default function JoinTrip({ tripId, inviteToken, onJoined, onAuth }) {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthResolved(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!tripId || !authResolved) return;
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const fetchTrip = async () => {
      try {
        const snap = await getDoc(doc(db, "trips", tripId));
        if (snap.exists()) {
          setTrip({ id: snap.id, ...snap.data() });
        } else {
          setError("This invite link is invalid or has expired.");
        }
      } catch (err) {
        setError("Something went wrong. Please try again.");
      }
      setLoading(false);
    };
    fetchTrip();
  }, [tripId, authResolved, user]);

  const handleJoin = async () => {
    if (!user) {
      onAuth();
      return;
    }

    if (!inviteToken) {
      setError("Invalid invite link — please ask the trip creator for a new link.");
      return;
    }

    if (!trip) {
      console.error("JoinTrip.handleJoin: trip is null — fetch may not have completed");
      setError("Couldn't load the trip. Try the link again.");
      return;
    }

    setJoining(true);
    try {
      const joinTripFn = httpsCallable(functions, "joinTrip");
      const result = await joinTripFn({ tripId, token: inviteToken });
      const { alreadyMember } = result.data;

      if (alreadyMember) {
        setAlreadyJoined(true);
      }
      setJoined(true);
      setTimeout(async () => {
        try {
          const freshSnap = await getDoc(doc(db, "trips", tripId));
          onJoined(freshSnap.exists() ? { id: freshSnap.id, ...freshSnap.data() } : trip);
        } catch (_) {
          onJoined(trip);
        }
      }, 1500);
    } catch (err) {
      console.error(err);
      const code = err.code;
      if (code === "functions/unauthenticated") {
        setError("Please sign in first.");
      } else if (code === "functions/invalid-argument") {
        setError("Invalid invite link.");
      } else if (code === "functions/not-found") {
        setError("Trip not found — it may have been deleted.");
      } else if (code === "functions/permission-denied") {
        setError("This invite link is no longer valid.");
      } else {
        setError("Failed to join trip. Please try again.");
      }
    }
    setJoining(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .jt-root {
          min-height: 100vh;
          background: #FAF6F0;
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .jt-logo {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 600;
          color: #1a1714;
          letter-spacing: -0.02em;
          margin-bottom: 32px;
        }

        .jt-logo span { color: #F5A623; }

        .jt-card {
          background: white;
          border-radius: 20px;
          border: 0.5px solid rgba(26,23,20,0.08);
          padding: 36px 32px;
          max-width: 420px;
          width: 100%;
          text-align: center;
          box-shadow: 0 4px 32px rgba(26,23,20,0.08);
          animation: fadeUp 0.4s ease forwards;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .jt-invite-icon {
          width: 64px;
          height: 64px;
          border-radius: 20px;
          background: #e3eef6;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 28px;
        }

        .jt-eyebrow {
          display: inline-block;
          background: #e3eef6;
          color: #F5A623;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 4px 10px;
          border-radius: 100px;
          margin-bottom: 14px;
        }

        .jt-title {
          font-family: 'Playfair Display', serif;
          font-size: 24px;
          font-weight: 600;
          color: #1a1714;
          letter-spacing: -0.02em;
          margin-bottom: 8px;
          line-height: 1.2;
        }

        .jt-title em { font-style: italic; color: #F5A623; }

        .jt-sub {
          font-size: 14px;
          color: rgba(26,23,20,0.5);
          font-weight: 300;
          line-height: 1.6;
          margin-bottom: 24px;
        }

        .jt-trip-info {
          background: #FAF6F0;
          border-radius: 14px;
          padding: 16px;
          margin-bottom: 24px;
          text-align: left;
        }

        .jt-trip-name {
          font-size: 16px;
          font-weight: 500;
          color: #1a1714;
          margin-bottom: 4px;
        }

        .jt-trip-meta {
          font-size: 13px;
          color: rgba(26,23,20,0.5);
          font-weight: 300;
        }

        .jt-members {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 10px;
        }

        .jt-member-av {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: #1E2D4A;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          font-weight: 500;
          color: white;
          border: 2px solid white;
          margin-left: -6px;
        }

        .jt-member-av:first-child { margin-left: 0; }

        .jt-member-count {
          font-size: 12px;
          color: rgba(26,23,20,0.45);
          margin-left: 6px;
        }

        .jt-join-btn {
          width: 100%;
          padding: 14px;
          background: #F5A623;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
          margin-bottom: 12px;
        }

        .jt-join-btn:hover:not(:disabled) {
          background: #1E2D4A;
          transform: translateY(-1px);
        }

        .jt-join-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .jt-note {
          font-size: 12px;
          color: rgba(26,23,20,0.4);
          line-height: 1.5;
        }

        .jt-success {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .jt-success-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #E1F5EE;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
        }

        .jt-success-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 600;
          color: #1a1714;
        }

        .jt-success-sub {
          font-size: 14px;
          color: rgba(26,23,20,0.5);
          font-weight: 300;
        }

        .jt-error {
          background: #fef0ed;
          border: 1px solid rgba(201,98,47,0.2);
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 13px;
          color: #c9622f;
          margin-bottom: 16px;
        }

        .jt-loading {
          font-size: 14px;
          color: rgba(26,23,20,0.4);
        }
      `}</style>

      <div className="jt-root">
        <div className="jt-logo">Solis</div>

        <div className="jt-card">
          {loading ? (
            <p className="jt-loading">Loading trip details...</p>
          ) : error ? (
            <div className="jt-error">{error}</div>
          ) : joined ? (
            <div className="jt-success">
              <div className="jt-success-icon">{alreadyJoined ? "✅" : "🎉"}</div>
              <div className="jt-success-title">{alreadyJoined ? "Already a member!" : "You're in!"}</div>
              <div className="jt-success-sub">Taking you to the trip...</div>
            </div>
          ) : !user ? (
            <>
              <div className="jt-invite-icon">✈️</div>
              <div className="jt-eyebrow">You're invited</div>
              <h2 className="jt-title">
                Join a <em>Solis</em> trip
              </h2>
              <p className="jt-sub">
                You've been invited to join a group trip on Solis. Sign in to see the details and accept.
              </p>
              {error && <div className="jt-error">{error}</div>}
              <button
                className="jt-join-btn"
                onClick={handleJoin}
              >
                Sign in to join →
              </button>
              <p className="jt-note">You'll need a free Solis account to join. Takes 2 minutes!</p>
            </>
          ) : (
            <>
              <div className="jt-invite-icon">✈️</div>
              <div className="jt-eyebrow">You're invited</div>
              <h2 className="jt-title">
                Join <em>{trip?.name}</em>
              </h2>
              <p className="jt-sub">
                {trip?.createdByName || "Someone"} invited you to join their Solis group trip.
              </p>

              <div className="jt-trip-info">
                <div className="jt-trip-name">{trip?.name}</div>
                <div className="jt-trip-meta">
                  {getFlagEmoji(trip?.countryCode) || '📍'} {trip?.destination}, {trip?.country}
                </div>
                <div className="jt-trip-meta">
                  📅 {formatDate(trip?.startDate)} → {formatDate(trip?.endDate)} · {trip?.days} days
                </div>
                <div className="jt-members">
                  {[...Array(Math.min(trip?.memberUids?.length || 1, 4))].map((_, i) => (
                    <div key={i} className="jt-member-av" style={{
                      background: ['#1E2D4A','#1D9E75','#C9622F','#BA7517'][i]
                    }}>
                      {String.fromCharCode(65 + i)}
                    </div>
                  ))}
                  <span className="jt-member-count">
                    {trip?.memberUids?.length || 1} traveler{(trip?.memberUids?.length || 1) !== 1 ? 's' : ''} joined
                  </span>
                </div>
              </div>

              {error && <div className="jt-error">{error}</div>}

              <button
                className="jt-join-btn"
                onClick={handleJoin}
                disabled={joining}
              >
                {joining ? "Joining..." : "Join this trip 🌍"}
              </button>

              <p className="jt-note">
                Your travel profile will be added to this trip so the AI can plan around your preferences.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}